import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/shared/ui/icon';
import { DropdownMenu, MenuDivider, MenuItem, MenuLabel } from '@/shared/ui/dropdown-menu';

import type { ListPageViewsApi } from '../model/useListPageViews';
import type { ListViewDef } from '../model/viewsModel';
import { ViewDialog, type ViewDialogState } from './ViewDialog';

/**
 * Řádek pohledů (anatomie 01 §2) — záložky uložených pohledů: systémové „Vše", šablonové
 * a serverové (👥 = sdílené, tečka = dirty), kebab na aktivním nesystémovém pohledu,
 * drag & drop pořadí (nativní HTML5 DnD) a „Uložit pohled".
 */
export function ViewsBar({ views: api }: { views: ListPageViewsApi }) {
  const { t } = useTranslation('listPage');
  const [dialog, setDialog] = useState<ViewDialogState | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const submitDialog = async (data: { name: string; shared: boolean }) => {
    if (!dialog) {
      return false;
    }
    if (dialog.mode === 'create') {
      return api.saveNew(data.name, data.shared);
    }
    return dialog.viewId ? api.rename(dialog.viewId, data.name, data.shared) : false;
  };

  const kebab = (view: ListViewDef) => (
    <DropdownMenu
      align="start"
      trigger={
        <button type="button" className="phxlist-view-kebab" title={t('views.manage')}>
          <Icon name="more" size={13} />
        </button>
      }
    >
      <MenuLabel>{view.label}</MenuLabel>
      {view.own && api.dirty ? (
        <MenuItem icon="save" onClick={() => void api.saveChanges()}>
          {t('views.saveChanges')}
        </MenuItem>
      ) : null}
      {view.own ? (
        <MenuItem
          icon="edit"
          onClick={() =>
            setDialog({ mode: 'edit', viewId: view.id, name: view.label, shared: view.shared })
          }
        >
          {t('views.renameVisibility')}
        </MenuItem>
      ) : null}
      <MenuItem icon="copy" onClick={() => void api.duplicate(view.id)}>
        {t('views.duplicate')}
      </MenuItem>
      {view.own ? (
        <>
          <MenuDivider />
          <MenuItem icon="trash" tone="danger" onClick={() => void api.remove(view.id)}>
            {t('views.delete')}
          </MenuItem>
        </>
      ) : null}
    </DropdownMenu>
  );

  return (
    <>
      <div className="phxlist-views">
        <div className="phxlist-views-tabs">
          {api.views.map((view) => {
            const active = view.id === api.activeViewId;
            // Přetažitelné jsou všechny záložky včetně „Vše" — vlastní pohledy tak jdou dát před „Vše".
            const draggable = true;
            return (
              <span
                key={view.id}
                className="phxlist-view-tab"
                data-active={active || undefined}
                data-drag={dragId === view.id || undefined}
                data-over={(overId === view.id && dragId !== view.id) || undefined}
                draggable={draggable}
                onDragStart={(e) => {
                  setDragId(view.id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (overId !== view.id) {
                    setOverId(view.id);
                  }
                }}
                onDragLeave={() => {
                  if (overId === view.id) {
                    setOverId(null);
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragId) {
                    api.reorder(dragId, view.id);
                  }
                  setDragId(null);
                  setOverId(null);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
              >
                <button
                  type="button"
                  className="phxlist-view-tab-label"
                  onClick={() => api.pick(view.id)}
                >
                  {view.shared ? (
                    <span className="phxlist-view-shared" title={t('views.sharedTitle')}>
                      <Icon name="users" size={12} />
                    </span>
                  ) : null}
                  {view.label}
                  {active && api.dirty ? (
                    <span className="phxlist-view-dirty" title={t('views.dirtyTitle')} />
                  ) : null}
                </button>
                {active && !view.system && !view.template ? kebab(view) : null}
              </span>
            );
          })}
        </div>
        <button
          type="button"
          className="phxlist-view-save"
          title={t('views.saveNewTitle')}
          onClick={() => setDialog({ mode: 'create', name: '', shared: false })}
        >
          <Icon name="save" size={12} />
          {t('views.saveNew')}
        </button>
      </div>
      <ViewDialog state={dialog} onClose={() => setDialog(null)} onSubmit={submitDialog} />
    </>
  );
}
